/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {UiNavigationBarButtonConfig} from "../generated/UiNavigationBarButtonConfig";
import {UiComponent} from "./UiComponent";
import {Renderer} from "./Common";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {UiNavigationBar_ButtonClickedEvent, UiNavigationBarCommandHandler, UiNavigationBarConfig, UiNavigationBarEventSource} from "../generated/UiNavigationBarConfig";
import {EventFactory} from "../generated/EventFactory";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
interface Button {
	data: any;
	$button: JQuery;
}

export class UiNavigationBar extends UiComponent<UiNavigationBarConfig> implements UiNavigationBarCommandHandler, UiNavigationBarEventSource {

	public readonly onButtonClicked: TeamAppsEvent<UiNavigationBar_ButtonClickedEvent> = new TeamAppsEvent<UiNavigationBar_ButtonClickedEvent>(this);

	private $bar: JQuery;
	private $buttonsWrapper: JQuery;
	private buttons: { [id: string]: Button } = {};
	private buttonTemplateRenderer: Renderer;
	private $fanOutContainerWrapper: JQuery;
	private $fanOutContainer: JQuery;
	private fanOutComponents: { [id: string]: UiComponent<UiComponentConfig> } = {};
	private currentFanOutComponent: UiComponent<UiComponentConfig>;

	constructor(config: UiNavigationBarConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.buttonTemplateRenderer = context.templateRegistry.createTemplateRenderer(config.buttonTemplate, null);

		this.$bar = $(`<div class="UiNavigationBar">
                <div class="fan-out-container-wrapper teamapps-blurredBackgroundImage">
                    <div class="fan-out-container"/>
                </div>
                <div class="buttons-wrapper"/>
            </div>`);
		this.$buttonsWrapper = this.$bar.find(">.buttons-wrapper");
		this.$fanOutContainerWrapper = this.$bar.find(">.fan-out-container-wrapper");
		this.$fanOutContainer = this.$fanOutContainerWrapper.find(">.fan-out-container");

		if (config.backgroundColor) {
			this.$buttonsWrapper.css("background-color", config.backgroundColor);
		}
		if (config.borderColor) {
			this.$bar.css("border-color", config.borderColor);
		}
		if (config.fanOutComponents) {
			config.fanOutComponents.forEach(c => this.addFanOutComponent(c));
		}

		config.buttons.forEach(button => this.addButton(button));

		this.reLayout();
	}

	public getMainDomElement(): JQuery {
		return this.$bar;
	}

	protected onAttachedToDom() {
		// do nothing.
	}

	public addButton(button: UiNavigationBarButtonConfig, neighborButtonId?: string, beforeNeighbor?: boolean) {
		let $button = $(`<div class="nav-button-wrapper">`);
		$button.append(this.buttonTemplateRenderer.render(button.data));
		$button.click(() => {
			this.onButtonClicked.fire(EventFactory.createUiNavigationBar_ButtonClickedEvent(this.getId(), button.id, this.currentFanOutComponent && this.currentFanOutComponent.getId()));
		});
		this.buttons[button.id] = {
			data: button.data,
			$button: $button
		};

		if (neighborButtonId && this.buttons[neighborButtonId]) {
			if (beforeNeighbor) {
				$button.insertBefore(this.buttons[neighborButtonId].$button);
			} else {
				$button.insertAfter(this.buttons[neighborButtonId].$button);
			}
		} else {
			this.$buttonsWrapper.append($button);
		}
	}

	public removeButton(buttonId: string): void {
		let button = this.buttons[buttonId];
		delete this.buttons[buttonId];
		button.$button.remove();
	}

	public setButtonVisible(buttonId: string, visible: boolean) {
		let button = this.buttons[buttonId];
		if (button) {
			button.$button.toggleClass("hidden", !visible);
		}
	}

	public addFanOutComponent(fanOutComponentConfig: UiComponentConfig) {
		if (this.fanOutComponents[fanOutComponentConfig.id]) {
			this.removeFanOutComponent(fanOutComponentConfig.id);
		}
		let fanOutComponent = this._context.createComponent(fanOutComponentConfig);
		fanOutComponent.getMainDomElement().addClass("pseudo-hidden").appendTo(this.$fanOutContainer);
		this.fanOutComponents[fanOutComponentConfig.id] = fanOutComponent;
	};

	public removeFanOutComponent(fanOutComponentId: string) {
		let fanOutComponent = this.fanOutComponents[fanOutComponentId];
		if (fanOutComponent == null) {
			return;
		}
		fanOutComponent.getMainDomElement().remove();
		fanOutComponent.destroy();
		delete this.fanOutComponents[fanOutComponentId];
	};

	public showFanOutComponent(fanOutComponentId: string) {
		const showFanout = () => {
			this.currentFanOutComponent = this.fanOutComponents[fanOutComponentId];
			Object.keys(this.fanOutComponents).forEach(id => this.fanOutComponents[id].getMainDomElement().addClass("pseudo-hidden"));
			this.currentFanOutComponent.getMainDomElement().removeClass("pseudo-hidden");
			this.currentFanOutComponent.attachedToDom = true;
			this.$fanOutContainerWrapper.addClass("open");
			this.$fanOutContainerWrapper.css("bottom", this.$bar.outerHeight(true) + "px");
			this.$fanOutContainerWrapper.slideDown(200);
			this.onResize();
		};
		if (this.currentFanOutComponent) {
			this.hideFanOutComponent();
			setTimeout(showFanout, 210);
		} else {
			showFanout();
		}
	}

	public hideFanOutComponent() {
		this.$fanOutContainerWrapper.removeClass("open");
		this.$fanOutContainerWrapper.slideUp(200);
		this.currentFanOutComponent = null;
	}

	public onResize(): void {
		if (this.$fanOutContainerWrapper.is(".open")) {
			let $clippingParent = this.findNearestParentWithHiddenVerticalOverflow(this.$fanOutContainerWrapper);
			let maxFanOutHeight = this.$bar.offset().top - $clippingParent.offset().top;
			if (maxFanOutHeight <= 0) {
				this.logger.warn("Fanout height is 0 due to clipping parent component...");
			}
			this.$fanOutContainerWrapper.css("max-height", maxFanOutHeight + "px")[0].offsetHeight;
			this.currentFanOutComponent && this.currentFanOutComponent.reLayout();
		}
	}

	private findNearestParentWithHiddenVerticalOverflow(child: JQuery): JQuery {
		let el = child[0];
		while (el != null) {
			el = el.parentElement;
			if ($(el).css("overflow-y") !== "visible") {
				break;
			}
		}
		return $(el);
	}

	public destroy(): void {
		// do nothing
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiNavigationBar", UiNavigationBar);